/**
 * VendorDashboard Component
 * 
 * Dashboard view specifically designed for Vendor users.
 * Shows only repair-related data and metrics relevant to the vendor.
 * 
 * Features:
 * - Repairs received from customers
 * - Repair statuses (pending, in progress, complete)
 * - Counts and metrics for repairs attached to the vendor
 */

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, PieChart, Pie, ResponsiveContainer
} from 'recharts';
import { Clock, CheckCircle, Wrench, AlertCircle, Package, TrendingUp } from 'lucide-react';
import StatusBadge from './StatusBadge';

export default function VendorDashboard() {
  const { repairs, loading } = useApi();
  useAuth(); // For context availability
  const { isDark } = useTheme();

  // Filter repairs to show only those assigned to this vendor
  // In a real app, you'd filter by vendor_id matching the current user's organization
  const vendorRepairs = useMemo(() => {
    // For now, show all repairs - in production, filter by vendor_id
    return repairs || [];
  }, [repairs]);

  // Calculate repair statistics
  const repairStats = useMemo(() => {
    const stats = {
      total: vendorRepairs.length,
      sentToVendor: 0,
      needsApproval: 0,
      inRepair: 0,
      complete: 0,
      finalized: 0,
    };

    vendorRepairs.forEach(repair => {
      switch (repair.status) {
        case 'Needs Repair':
        case 'Sent to Vendor':
          stats.sentToVendor++;
          break;
        case 'Needs Approval':
          stats.needsApproval++;
          break;
        case 'In Repair':
          stats.inRepair++;
          break;
        case 'Complete':
          stats.complete++;
          break;
        case 'Finalized':
          stats.finalized++;
          break;
        default:
          break;
      }
    });

    return stats;
  }, [vendorRepairs]);

  // Priority breakdown
  const priorityStats = useMemo(() => {
    const stats = { Low: 0, Medium: 0, High: 0, Critical: 0 };
    vendorRepairs.forEach(repair => {
      const priority = repair.priority || 'Medium';
      if (stats.hasOwnProperty(priority)) {
        stats[priority]++;
      }
    });
    return Object.entries(stats).map(([name, value]) => ({ name, value }));
  }, [vendorRepairs]);

  // Status breakdown for pie chart
  const statusChartData = useMemo(() => {
    return [
      { name: 'Sent to Vendor', value: repairStats.sentToVendor, color: '#f59e0b' },
      { name: 'Needs Approval', value: repairStats.needsApproval, color: '#eab308' },
      { name: 'In Repair', value: repairStats.inRepair, color: '#3b82f6' },
      { name: 'Complete', value: repairStats.complete, color: '#10b981' },
    ].filter(item => item.value > 0);
  }, [repairStats]);

  // Recent repairs (last 10)
  const recentRepairs = useMemo(() => {
    return [...vendorRepairs]
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .slice(0, 10);
  }, [vendorRepairs]);

  // Pending actions (repairs needing attention)
  const pendingActions = useMemo(() => {
    return vendorRepairs
      .filter(r => r.status === 'Needs Repair' || r.status === 'Sent to Vendor' || r.status === 'Needs Approval')
      .sort((a, b) => {
        const priorityOrder = { Critical: 0, High: 1, Medium: 2, Low: 3 };
        return (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
      })
      .slice(0, 5);
  }, [vendorRepairs]);

  if (loading && vendorRepairs.length === 0) {
    return (
      <div className={`h-full flex items-center justify-center ${isDark ? 'bg-slate-900 text-slate-400' : 'bg-slate-50 text-slate-500'}`}>
        Loading vendor dashboard...
      </div>
    );
  }

  const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
    <div className={`p-5 rounded-xl border transition-colors ${isDark ? 'bg-gray-800/30 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm'}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-sm font-medium ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{title}</p>
          <h3 className={`text-3xl font-bold mt-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{value}</h3>
          {subtitle && (
            <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{subtitle}</p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon size={22} className="text-white" />
        </div>
      </div>
      {trend && (
        <div className="mt-3 flex items-center gap-1 text-xs">
          <TrendingUp size={14} className="text-emerald-400" />
          <span className="text-emerald-400">{trend}</span>
        </div>
      )}
    </div>
  );

  return (
    <div className={`p-6 h-full overflow-y-auto space-y-6 ${isDark ? 'bg-slate-900 text-slate-100' : 'bg-slate-50 text-slate-900'}`}>
      <style>{`
        .recharts-wrapper,
        .recharts-wrapper:focus,
        .recharts-wrapper svg,
        .recharts-wrapper svg:focus {
          outline: none !important;
          box-shadow: none !important;
        }
        .recharts-wrapper * {
          outline: none !important;
        }
        .recharts-pie path {
          stroke: none !important;
          stroke-width: 0 !important;
        }
        .recharts-tooltip-wrapper {
          pointer-events: none;
        }
      `}</style>
      {/* Header */}
      <header className="mb-2">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-amber-500/20 rounded-lg">
            <Wrench className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Vendor Dashboard</h1>
            <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Repair Management Overview</p>
          </div>
        </div>
      </header>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Total Repairs" 
          value={repairStats.total} 
          subtitle="All time"
          icon={Package} 
          color="bg-indigo-500" 
        />
        <StatCard 
          title="Pending Repairs" 
          value={repairStats.sentToVendor + repairStats.needsApproval} 
          subtitle="Awaiting action"
          icon={Clock} 
          color="bg-amber-500" 
        />
        <StatCard 
          title="In Progress" 
          value={repairStats.inRepair} 
          subtitle="Currently repairing"
          icon={Wrench} 
          color="bg-blue-500" 
        />
        <StatCard 
          title="Completed" 
          value={repairStats.complete + repairStats.finalized} 
          subtitle="Successfully repaired"
          icon={CheckCircle} 
          color="bg-emerald-500" 
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status Distribution Pie Chart */}
        <div className={`rounded-xl p-6 border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <h3 className={`text-lg font-semibold mb-6 text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>Repair Status</h3>
          <div className="w-full h-48 sm:h-56 outline-none focus:outline-none">
            {statusChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    innerRadius={40}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="none"
                  >
                    {statusChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip 
                    allowEscapeViewBox={{ x: true, y: true }}
                    position={{ x: 'auto', y: 'auto' }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0];
                        const entry = statusChartData.find(item => item.name === data.name);
                        const color = entry?.color || '#8884d8';
                        const total = statusChartData.reduce((sum, item) => sum + item.value, 0);
                        const percent = ((data.value / total) * 100).toFixed(0);
                        return (
                          <div 
                            style={{ 
                              backgroundColor: color,
                              color: '#ffffff',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              border: 'none',
                              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                              marginLeft: '20px',
                              marginTop: '-10px'
                            }}
                          >
                            <div style={{ fontWeight: '600', fontSize: '14px' }}>
                              {data.name}
                            </div>
                            <div style={{ fontSize: '12px', opacity: 0.9 }}>
                              {data.value} ({percent}%)
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className={`h-full flex items-center justify-center text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                No repair data
              </div>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-3 justify-center">
            {statusChartData.map((entry, index) => (
              <div key={index} className={`flex items-center gap-2 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <span 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: entry.color }}
                ></span>
                <span>{entry.name}</span>
                <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>({entry.value})</span>
              </div>
            ))}
          </div>
        </div>

        {/* Priority Breakdown */}
        <div className={`lg:col-span-2 rounded-xl p-6 border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <h3 className={`text-lg font-semibold mb-6 text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>Priority Breakdown</h3>
          <div className="w-full h-48 sm:h-56 md:h-64 outline-none focus:outline-none">
            {priorityStats.some(p => p.value > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={priorityStats}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                  <XAxis dataKey="name" stroke={isDark ? '#94a3b8' : '#64748b'} fontSize={12} tickLine={false} />
                  <YAxis stroke={isDark ? '#94a3b8' : '#64748b'} fontSize={12} tickLine={false} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isDark ? '#1e293b' : '#ffffff', 
                      borderColor: isDark ? '#334155' : '#e2e8f0', 
                      color: isDark ? '#f8fafc' : '#0f172a' 
                    }}
                    cursor={{ fill: isDark ? '#334155' : '#f1f5f9', opacity: 0.4 }}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {priorityStats.map((entry, index) => {
                      const colors = {
                        Low: '#3b82f6',
                        Medium: '#f59e0b',
                        High: '#f97316',
                        Critical: '#ef4444'
                      };
                      return <Cell key={`cell-${index}`} fill={colors[entry.name] || '#6366f1'} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className={`h-full flex items-center justify-center text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                No repair data available
              </div>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            {priorityStats.map((entry, index) => {
              const colors = {
                Low: '#3b82f6',
                Medium: '#f59e0b',
                High: '#f97316',
                Critical: '#ef4444'
              };
              return (
                <div key={index} className={`flex items-center gap-2 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: colors[entry.name] || '#6366f1' }}
                  ></span>
                  {entry.name}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Actions */}
        <div className={`rounded-xl overflow-hidden border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <div className={`p-4 border-b flex items-center justify-between ${isDark ? 'border-slate-800' : 'border-gray-100'}`}>
            <h3 className={`font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <AlertCircle size={18} className="text-amber-400" />
              Pending Actions
            </h3>
            <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded-full">
              {pendingActions.length} items
            </span>
          </div>
          <div className={`divide-y ${isDark ? 'divide-slate-800' : 'divide-gray-100'}`}>
            {pendingActions.length > 0 ? (
              pendingActions.map((repair) => (
                <div key={repair.id} className={`p-4 transition-colors ${isDark ? 'hover:bg-slate-800/30' : 'hover:bg-slate-50'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-sm text-blue-500">
                          #{repair.id?.slice(0, 8)}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          repair.priority === 'Critical' || repair.priority === 'High' 
                            ? 'bg-red-900/30 text-red-400' 
                            : 'bg-amber-900/30 text-amber-400'
                        }`}>
                          {repair.priority || 'Medium'}
                        </span>
                      </div>
                      <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{repair.title}</p>
                      <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        {new Date(repair.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <StatusBadge status={repair.status} size="sm" />
                  </div>
                </div>
              ))
            ) : (
              <div className={`p-8 text-center text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-500/50" />
                No pending actions
              </div>
            )}
          </div>
        </div>

        {/* Recent Repairs */}
        <div className={`rounded-xl overflow-hidden border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <div className={`p-4 border-b flex items-center justify-between ${isDark ? 'border-slate-800' : 'border-gray-100'}`}>
            <h3 className={`font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Clock size={18} className="text-blue-400" />
              Recent Repairs
            </h3>
            <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              Last 10 repairs
            </span>
          </div>
          <div className={`divide-y max-h-[350px] overflow-y-auto ${isDark ? 'divide-slate-800' : 'divide-gray-100'}`}>
            {recentRepairs.length > 0 ? (
              recentRepairs.map((repair) => (
                <div key={repair.id} className={`p-3 transition-colors ${isDark ? 'hover:bg-slate-800/30' : 'hover:bg-slate-50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{repair.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`font-mono text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          #{repair.id?.slice(0, 6)}
                        </span>
                        <span className={`text-xs ${isDark ? 'text-slate-600' : 'text-slate-300'}`}>•</span>
                        <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          {new Date(repair.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <StatusBadge status={repair.status} size="sm" />
                  </div>
                </div>
              ))
            ) : (
              <div className={`p-8 text-center text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                No repairs yet
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

